import type { ReactNode } from 'react'
import type { CustomerAuthField } from '../../hooks/customer-auth-form/validation'
import type { CustomerAuthFieldUiHelpers } from './form-sections'
import { AnimatePresence, motion } from 'motion/react'

const inputBaseClassName =
  'ca-input ca-theme-transition h-11 w-full rounded-lg border-(--color-border-default) bg-(--color-bg-card) px-3 text-sm text-(--color-text-primary) shadow-[0_1px_2px_color-mix(in_srgb,var(--color-text-primary)_8%,transparent)] placeholder:text-(--color-text-muted) focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-focus-ring)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-70'

export const buildFieldUiHelpers = ({
  fieldErrors,
  onFieldBlur,
}: {
  fieldErrors: Partial<Record<CustomerAuthField, string>>
  onFieldBlur: (field: CustomerAuthField) => void
}): CustomerAuthFieldUiHelpers => {
  const fieldLabelRequiredMark: ReactNode = <span className="ml-1 text-(--color-status-error)">*</span>
  const resolveFieldError = (field: CustomerAuthField) => fieldErrors[field] ?? ''
  const resolveFieldErrorId = (field: CustomerAuthField) => `customer-auth-field-error-${field}`

  const resolveInputClassName = (field: CustomerAuthField, extraClassName = '') => {
    const errorClassName = resolveFieldError(field)
      ? 'border-(--color-status-error-border) bg-(--color-status-error-bg)'
      : ''
    return `${inputBaseClassName} ${errorClassName} ${extraClassName}`.trim()
  }

  const resolveSelectClassName = (field: CustomerAuthField) =>
    [
      'ca-input h-11 w-full rounded-lg border-(--color-border-default) bg-(--color-bg-card) shadow-[0_1px_2px_color-mix(in_srgb,var(--color-text-primary)_8%,transparent)] focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-focus-ring)_30%,transparent)]',
      resolveFieldError(field)
        ? 'border-(--color-status-error-border) bg-(--color-status-error-bg)'
        : '',
    ]
      .filter(Boolean)
      .join(' ')

  const renderFieldHint = (field: CustomerAuthField) => {
    const message = resolveFieldError(field)
    return (
      <span
        id={resolveFieldErrorId(field)}
        aria-live={message ? 'polite' : undefined}
        className={`mt-px block min-h-[1rem] text-[11px] ${message ? 'text-(--color-status-error)' : 'text-transparent'}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          {message ? (
            <motion.div
              key={message}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              {message}
            </motion.div>
          ) : (
            <span key="empty">{'\u00A0'}</span>
          )}
        </AnimatePresence>
      </span>
    )
  }

  return {
    fieldLabelRequiredMark,
    onFieldBlur,
    resolveFieldError,
    resolveErrorDescribedBy: (field) => (resolveFieldError(field) ? resolveFieldErrorId(field) : undefined),
    resolveInputClassName,
    resolveSelectClassName,
    renderFieldHint,
  }
}
